'use client';
// L'ONGLET MAINTENANCE D'UN APPAREIL (spec 002, récit 1, 03/11/2026).
//
// « Tout savoir d'un appareil sans quitter son dossier » : en tête le verdict
// (à jour / N en retard / hors service / sans plan) et LA prochaine échéance,
// puis quatre sous-onglets dans l'ordre où on les cherche :
//
//   · Dû         — plan et métrologie, retards d'abord, bouton « Faite » ;
//   · Plan       — les lignes de l'appareil, MÊMES colonnes que l'écran
//                  Maintenances (`colonnesPlan`) ;
//   · Historique — tout ce qui a été fait, typé (préventive, hors plan,
//                  curative, métrologie), chaque ligne ouvre son détail ;
//   · Supports   — vidéos, notices, documents de la GED liés.
//
// Il remplace les sous-onglets « Programmées » / « Échéances » (le composant
// `Maintenances` encastré) et la boucle Fiche de vie → Programmées → Fiche de
// vie : le détail d'une intervention s'ouvre ICI.
//
// ⚠️ AUCUN CALCUL DE DATE dans cet écran : le verdict et la prochaine échéance
// viennent du serveur (`etatMaintenanceAppareil`), le même calcul que la liste
// du parc, l'étiquette et les tâches (récit 7).
//
// Adresses : `?onglet=maintenance&ref=<ligne>@<date>` ouvre la déclaration de
// l'échéance ; `?onglet=maintenance&intervention=<id>` ouvre le détail d'une
// intervention.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGed } from '@/lib/store';
import { fmtDate } from '@/lib/meta';
import { I, card, EmptyState, Skeleton, Pastille, ConfirmButton } from '@/components/ui';
import { OngletsDossier, PanneauDossier } from '@/components/Dossier';
import { Tableau, ligneOuvrable, SansOuvrir } from '@/shared/Tableau';
import { Bouton } from '@/shared/Bouton';
import { VideoDemonstration } from '@/components/VideoDemonstration';
import { peutGererParc } from '@/lib/droits-parc';
import {
  libelleVerdict, tonVerdict, natureProchaine, echeanceDesignee, interventionDesignee, idIntervention,
  LIBELLE_GENRE, type MaintenanceAppareilReponse, type EntreeAppareil, type CibleMaintenance, type GenreIntervention,
} from '@/lib/maintenance-appareil';
import type { GedEquipement, MaintenanceLigne, MaintenanceOccurrence, MaintenanceRegistre } from '@/lib/types';
import {
  EtatPill, FormulaireLigne, LIGNE_VIDE, brouillonDe, colonnesPlan, prochaineDeLigne, quiEnRepondDe,
} from './Maintenances';
import { FenetreMaintenance, Resultat } from './MaintenancesHistorique';
import { SaisirMaintenance, type TypeSaisie } from './SaisirMaintenance';
import { LienFichier } from './EquipFiche';
import { RelierSupport } from './SupportsMaintenance';
import { direSertA, libelleTypeSupport } from '@/lib/supports-maintenance';
import { declarable, refEcheance } from '@/lib/declaration-maintenance';
import type { RefSupportMaintenance } from '@/lib/types';

type Sous = 'du' | 'plan' | 'historique' | 'supports';

/** Une ligne de « Ce qui est dû » : une échéance du plan, ou celle de la métrologie. */
interface LigneDu {
  cle: string;
  date: string;
  acte: string;
  nature: string;
  qui: string;
  etat: string;
  occ: MaintenanceOccurrence | null;
}

const RANG_ETAT: Record<string, number> = { en_retard: 0, sans_delai: 1, toleree: 2, due: 3, a_venir: 4, suspendue: 5 };
const TON_GENRE: Record<GenreIntervention, 'info' | 'neutral' | 'danger' | 'success'> = {
  preventive: 'info', hors_plan: 'neutral', curative: 'danger', metrologie: 'success',
};
const LIBELLE_ETAT_METRO: Record<string, { label: string; ton: 'danger' | 'info' | 'muted' }> = {
  en_retard: { label: 'en retard', ton: 'danger' },
  due: { label: 'à faire', ton: 'info' },
  a_venir: { label: 'à venir', ton: 'muted' },
};

/** Réécrit les paramètres de l'onglet dans l'adresse, SANS nouvelle entrée
 *  d'historique : ouvrir ou refermer une fenêtre n'est pas changer d'écran. */
function poserCibleDansAdresse(cible: CibleMaintenance) {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (url.searchParams.get('onglet') !== 'maintenance') return;
  for (const k of ['ref', 'intervention'] as const) {
    if (cible[k]) url.searchParams.set(k, cible[k] as string);
    else url.searchParams.delete(k);
  }
  window.history.replaceState({ ...(window.history.state || {}) }, '', url.pathname + url.search);
}

export function MaintenanceAppareil({ e, cible }: {
  e: GedEquipement;
  /** Ce que l'adresse (ou la Fiche de vie) demande d'ouvrir à l'arrivée. */
  cible?: CibleMaintenance;
}) {
  const { session, smq, ouvrirPanneau, loadMaintenances, ouvrirDeclaration } = useGed();
  const code = e.code || '';
  const peutParc = peutGererParc(session.roles);
  const peutDeclarer = session.roles.length > 0;

  const [rep, setRep] = useState<MaintenanceAppareilReponse | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [tour, setTour] = useState(0);
  const [sous, setSous] = useState<Sous>(cible?.intervention ? 'historique' : 'du');
  const [ouverteCle, setOuverteCle] = useState<string | null>(null);
  const [saisir, setSaisir] = useState<TypeSaisie | '' | null>(null);
  const [form, setForm] = useState<{ ligne: MaintenanceLigne | null } | null>(null);
  /* Le registre complet n'est lu que pour PLANIFIER : le formulaire d'une ligne
     a besoin des fréquences, des politiques de report et des postes. */
  const [reg, setReg] = useState<MaintenanceRegistre | null>(null);
  const [cibleVue, setCibleVue] = useState(false);
  /* Spec 002, récit 2 : le support qu'on relie à des lignes du plan. */
  const [relier, setRelier] = useState<{ support: RefSupportMaintenance; libelle: string } | null>(null);

  const lire = useCallback(async (): Promise<MaintenanceAppareilReponse> => {
    const r = await fetch(`/api/ged/ged/maintenances/equipement/${encodeURIComponent(code)}`, { credentials: 'include' });
    if (!r.ok) throw new Error(`La maintenance de l’appareil n’a pas pu être lue (${r.status}).`);
    return (await r.json()) as MaintenanceAppareilReponse;
  }, [code]);

  useEffect(() => {
    if (!code) return undefined;
    let vivant = true;
    void lire()
      .then((d) => { if (vivant) { setRep(d); setErreur(null); } })
      .catch((x: Error) => { if (vivant) { setRep(null); setErreur(x.message); } })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [lire, tour, code]);

  /** Relit l'onglet SANS le vider, et la projection du tableau de bord. */
  const recharger = useCallback(async () => {
    try { setRep(await lire()); } catch { /* l'écran garde la dernière lecture */ }
    await loadMaintenances();
  }, [lire, loadMaintenances]);

  /* « Déclarer faite » : la fenêtre unique (spec 002, R14), ouverte par le
     magasin ; l'adresse porte l'échéance tant qu'elle est ouverte. */
  const declarer = useCallback((o: MaintenanceOccurrence, lignes: MaintenanceLigne[]) => {
    poserCibleDansAdresse({ ref: refEcheance(o) });
    ouvrirDeclaration(refEcheance(o), {
      occ: o, ligne: lignes.find((l) => l.ref === o.maintenanceRef),
      apres: recharger, aLaFermeture: () => poserCibleDansAdresse({}),
    });
  }, [ouvrirDeclaration, recharger]);

  /* L'arrivée par adresse : une fois les données là, on ouvre ce qu'elle désigne
     — une seule fois. Différé d'une micro-tâche, comme l'écran Maintenances. */
  useEffect(() => {
    if (!rep || cibleVue || !cible) return undefined;
    const t = setTimeout(() => {
      setCibleVue(true);
      const o = echeanceDesignee(rep.echeances, cible.ref);
      if (o && declarable(o)) { setSous('du'); declarer(o, rep.lignes); }
      const iv = interventionDesignee(rep.historique, cible.intervention);
      if (iv) { setSous('historique'); setOuverteCle(iv.cle); }
    }, 0);
    return () => clearTimeout(t);
  }, [rep, cible, cibleVue, declarer]);

  const ouvrirPlanification = useCallback(async (ligne: MaintenanceLigne | null) => {
    if (!reg) {
      const r = await fetch(`/api/ged/ged/maintenances?equipement=${encodeURIComponent(code)}`, { credentials: 'include' });
      if (!r.ok) return;
      setReg((await r.json()) as MaintenanceRegistre);
    }
    setForm({ ligne });
  }, [reg, code]);

  const lignesDu = useMemo((): LigneDu[] => {
    if (!rep) return [];
    const quiDe = (o: MaintenanceOccurrence) => o.responsableLibelle || o.responsableKey
      || ((o.postesAOccuper || []).length ? `poste ${(o.postesAOccuper || []).join(' · ')} — non tenu` : 'aucun désigné');
    const out: LigneDu[] = rep.echeances.map((o) => ({
      cle: `${o.maintenanceRef}|${o.date}`, date: o.date, acte: o.libelle,
      nature: o.nature === 'verification' ? 'Vérification / contrôle' : 'Maintenance préventive',
      qui: quiDe(o), etat: o.etat, occ: o,
    }));
    const m = rep.etat?.metrologie;
    if (m?.date) {
      out.push({
        cle: 'metrologie', date: m.date, acte: m.source === 'posee' ? 'Étalonnage / vérification (date posée à la main)' : 'Étalonnage / vérification',
        nature: 'Métrologie', qui: e.responsableNom || 'responsable de l’appareil', etat: m.etat, occ: null,
      });
    }
    return out.sort((a, b) => (RANG_ETAT[a.etat] ?? 9) - (RANG_ETAT[b.etat] ?? 9) || a.date.localeCompare(b.date));
  }, [rep, e.responsableNom]);

  if (!code) {
    return <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>Cet équipement n’a pas de code d’inventaire : aucune maintenance ne peut lui être rattachée.</p>;
  }
  if (chargement) return <div style={{ ...card, overflow: 'hidden' }}><Skeleton rows={4} /></div>;
  if (!rep) {
    return (
      <EmptyState titre="La maintenance de l’appareil n’a pas pu être lue."
        indice={`${erreur || 'La requête n’a pas abouti.'} Ce n’est pas la même chose qu’un appareil sans maintenance : rien n’est affirmé sur son contenu.`}
        action={<Bouton taille="sm" onClick={() => { setChargement(true); setTour((t) => t + 1); }}>Réessayer</Bouton>} />
    );
  }

  const etat = rep.etat;
  const sansPlanAttendu = etat?.verdict === 'sans_objet';
  const qualiteAppareil = e.nature === 'logiciel' || e.categorie === 'logiciel' ? 'un logiciel' : 'un accessoire';
  const ouverte = rep.historique.find((x) => x.cle === ouverteCle) || null;
  const suivanteDe = (x: EntreeAppareil) => rep.historique
    .filter((y) => y.date && y.date > x.date && y.cle !== x.cle).map((y) => y.date).sort()[0] || '';
  const nbDu = lignesDu.filter((l) => l.etat !== 'a_venir').length;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/* ── LE VERDICT, et la prochaine échéance avec sa nature ─────────── */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <Pastille ton={tonVerdict(etat)} title={etat?.motif}>{libelleVerdict(etat)}</Pastille>
        <span style={{ fontSize: 12.5, color: 'var(--text-body)', flex: 1 }}>
          {etat?.prochaine
            ? <>Prochaine échéance : <strong style={{ fontFamily: 'var(--font-mono)' }}>{fmtDate(etat.prochaine.date)}</strong>
              <span style={{ color: 'var(--text-muted)' }}> — {natureProchaine(etat)}</span></>
            : <span style={{ color: 'var(--text-muted)' }}>{etat?.motif || 'Aucune échéance connue.'}</span>}
        </span>
        {peutDeclarer && (
          <Bouton taille="sm" onClick={() => setSaisir('')}>
            <I n="plus" s={13} />Enregistrer une intervention
          </Bouton>
        )}
        {peutParc && etat?.verdict !== 'reforme' && (
          <Bouton variante="primaire" taille="sm" onClick={() => void ouvrirPlanification(null)}>
            <I n="plus" s={13} />Planifier
          </Bouton>
        )}
      </div>
      {/* EF-005 : un appareil sans plan l'AFFIRME ; un accessoire ou un logiciel n'en attend pas. */}
      {etat?.verdict === 'sans_plan' && (
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--amber-600)' }}>
          Aucune maintenance planifiée sur cet appareil.{peutParc ? ' « Planifier » crée sa première ligne de plan.' : ''}
        </p>
      )}
      {sansPlanAttendu && (
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
          Aucun plan attendu pour {qualiteAppareil}. Une intervention peut toujours être enregistrée.
        </p>
      )}

      <OngletsDossier id="mnt-app" actif={sous} onChange={setSous} items={[
        { k: 'du' as Sous, label: 'Dû', n: nbDu || undefined },
        { k: 'plan' as Sous, label: 'Plan', n: rep.lignes.filter((l) => l.actif).length || undefined },
        { k: 'historique' as Sous, label: 'Historique', n: rep.historique.length || undefined },
        { k: 'supports' as Sous, label: 'Supports', n: rep.supports.length || undefined },
      ]} />

      <PanneauDossier id="mnt-app" actif={sous}>
        {sous === 'du' && (
          <Tableau
            id="maintenance-appareil-du"
            ajusterLargeur
            lignes={lignesDu}
            cleLigne={(l) => l.cle}
            vide={{
              titre: sansPlanAttendu ? `Aucun plan attendu pour ${qualiteAppareil}.`
                : rep.lignes.some((l) => l.actif) ? 'Rien n’est dû sur cet appareil.' : 'Aucune maintenance planifiée.',
              indice: rep.lignes.some((l) => l.actif)
                ? 'Les échéances des trois prochains mois et les retards des trois derniers apparaissent ici.'
                : sansPlanAttendu ? undefined : 'Sans plan, rien ne peut être dû : « Planifier » crée la première ligne.',
            }}
            colonnes={[
              { cle: 'date', libelle: 'Échéance', type: 'date', valeur: (l) => l.date,
                rendu: (l) => <span style={{ fontFamily: 'var(--font-mono)' }}>{fmtDate(l.date)}</span> },
              { cle: 'acte', libelle: 'Acte', valeur: (l) => l.acte, style: { whiteSpace: 'normal' }, rendu: (l) => l.acte },
              { cle: 'nature', libelle: 'Nature', type: 'liste', valeur: (l) => l.nature, rendu: (l) => l.nature },
              { cle: 'qui', libelle: 'Qui en répond', valeur: (l) => l.qui, style: { whiteSpace: 'normal' }, rendu: (l) => l.qui },
              { cle: 'etat', libelle: 'État', type: 'liste', valeur: (l) => l.etat,
                rendu: (l) => (l.occ
                  ? <EtatPill o={l.occ} />
                  : <Pastille ton={(LIBELLE_ETAT_METRO[l.etat] || LIBELLE_ETAT_METRO.a_venir).ton} compacte title={etat?.metrologie?.motif}>
                    {(LIBELLE_ETAT_METRO[l.etat] || LIBELLE_ETAT_METRO.a_venir).label}
                  </Pastille>) },
            ]}
            /* Une échéance à venir ne se déclare pas faite : on ne déclare pas fait ce qui n'a pas encore lieu. */
            actions={peutDeclarer ? (l) => (l.occ
              ? (declarable(l.occ)
                ? <Bouton taille="sm" onClick={() => { if (l.occ) declarer(l.occ, rep.lignes); }}>Faite</Bouton>
                : null)
              : (l.etat !== 'a_venir' ? <Bouton taille="sm" onClick={() => setSaisir('metrologie')}>Enregistrer</Bouton> : null)) : undefined}
          />
        )}

        {sous === 'plan' && (
          <Tableau
            id="plan-maintenance-appareil"
            niveau="standard"
            ajusterLargeur
            defilementHorizontal
            lignes={rep.lignes}
            cleLigne={(l) => l.documentId}
            vide={{
              titre: sansPlanAttendu ? `Aucun plan attendu pour ${qualiteAppareil}.` : 'Aucune maintenance planifiée.',
              indice: sansPlanAttendu ? undefined : 'Les fréquences viennent du laboratoire, pas de l’outil : « Planifier » crée la première ligne.',
            }}
            ligneProps={(l) => (peutParc
              ? ligneOuvrable(() => void ouvrirPlanification(l), `Modifier ${l.ref}`, { opacity: l.actif ? 1 : 0.55 })
              : { style: { opacity: l.actif ? 1 : 0.55 } })}
            colonnes={colonnesPlan({
              avecAppareil: false,
              quiEnRepond: (l) => quiEnRepondDe(l, rep.postes, rep.echeances),
              prochaine: (l) => prochaineDeLigne(l, rep.echeances),
            })}
            actions={peutParc ? (l) => (
              <SansOuvrir>
                <ConfirmButton label={l.actif ? 'Retirer du plan' : 'Remettre au plan'} variant="outline"
                  question={l.actif
                    ? 'Retirer cette maintenance du plan ? Elle cesse de produire des échéances. Les maintenances déjà réalisées restent dans l’historique de l’appareil.'
                    : 'Remettre cette maintenance au plan ? Elle recommence à produire des échéances.'}
                  onConfirm={async () => { await smq(`ged/maintenances/${l.documentId}/archiver`, { actif: !l.actif }); await recharger(); }} />
              </SansOuvrir>
            ) : undefined}
          />
        )}

        {sous === 'historique' && (
          <Tableau
            id="historique-maintenance-appareil"
            niveau="standard"
            ajusterLargeur
            defilementHorizontal
            lignes={rep.historique}
            cleLigne={(x) => x.cle}
            vide={{ titre: 'Aucune intervention enregistrée sur cet appareil.', indice: '« Enregistrer une intervention » consigne une préventive hors plan, une curative ou un étalonnage.' }}
            ligneProps={(x) => ligneOuvrable(() => { setOuverteCle(x.cle); poserCibleDansAdresse({ intervention: idIntervention(x) }); },
              `Voir le détail : ${x.intitule}`)}
            colonnes={[
              { cle: 'date', libelle: 'Date', type: 'date', valeur: (x) => x.date,
                rendu: (x) => (x.date
                  ? <span style={{ fontFamily: 'var(--font-mono)' }}>{fmtDate(x.date)}</span>
                  : <span style={{ color: 'var(--amber-600)' }} title={x.dateBrute ? `Date saisie : « ${x.dateBrute} »` : undefined}>illisible</span>) },
              { cle: 'genre', libelle: 'Type', type: 'liste', valeur: (x) => LIBELLE_GENRE[x.genre],
                rendu: (x) => <Pastille ton={TON_GENRE[x.genre]} compacte>{LIBELLE_GENRE[x.genre]}</Pastille> },
              { cle: 'fait', libelle: 'Ce qui a été fait', valeur: (x) => `${x.intitule} ${x.commentaire}`, style: { whiteSpace: 'normal' },
                rendu: (x) => <>{x.intitule}{x.commentaire && <span style={{ color: 'var(--text-muted)' }}> · {x.commentaire.length > 90 ? `${x.commentaire.slice(0, 90)}…` : x.commentaire}</span>}</> },
              { cle: 'par', libelle: 'Réalisé par', type: 'liste', valeur: (x) => x.intervenant || '',
                rendu: (x) => x.intervenant || <span style={{ color: 'var(--text-muted)' }}>non dit</span> },
              { cle: 'resultat', libelle: 'Résultat', valeur: (x) => (x.probleme ? 'problème' : x.conforme === true ? 'conforme' : ''),
                rendu: (x) => <Resultat e={x} /> },
              { cle: 'pieces', libelle: 'Pièces', valeur: (x) => x.nbFichiers, rendu: (x) => x.nbFichiers || '—' },
            ]}
          />
        )}

        {sous === 'supports' && (
          rep.supports.length === 0
            ? <EmptyState titre="Aucun support de maintenance."
              indice="Une notice ou une vidéo versée dans l’onglet Documents, ou un document de la GED lié à l’appareil, apparaît ici ; « Relier à une maintenance » la rattache à une ligne du plan." />
            : (
              <div style={{ ...card, padding: '6px 14px' }}>
                {rep.supports.map((s) => (
                  <div key={s.cle} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '8px 0', borderTop: '1px solid var(--border-subtle)', fontSize: 12.5 }}>
                    <Pastille ton="neutral" compacte>{libelleTypeSupport(s)}</Pastille>
                    {s.type === 'document'
                      ? (
                        <>
                          <Bouton taille="sm" variante="discret" onClick={() => ouvrirPanneau({ type: 'doc', valeur: s.code, origine: code })}
                            style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{s.code}</Bouton>
                          <span style={{ color: 'var(--text-body)' }}>{s.libelle || 'document introuvable au catalogue'}</span>
                          {s.version && <span style={{ color: 'var(--text-muted)' }}>v{s.version}</span>}
                        </>
                      )
                      : s.nature === 'video' && s.piece.url
                        ? <VideoDemonstration url={s.piece.url} libelle={s.libelle} />
                        : s.piece.cle
                          ? <LienFichier f={s.piece} />
                          : <span style={{ color: 'var(--text-body)' }}>{s.libelle}{s.piece.reference ? ` — ${s.piece.reference}` : ''} <span style={{ color: 'var(--text-muted)' }}>(référence seule)</span></span>}
                    <span style={{ flex: 1 }} />
                    <span style={{ color: 'var(--text-muted)' }}>{direSertA(s.sertA, s.commun)}</span>
                    {peutParc && rep.lignes.length > 0 && (
                      <Bouton taille="sm" onClick={() => setRelier({
                        support: s.type === 'document' ? { type: 'document', code: s.code } : { type: 'piece', id: s.piece.id },
                        libelle: s.type === 'document' ? `${s.code} — ${s.libelle}` : s.libelle,
                      })}>Relier à une maintenance</Bouton>
                    )}
                  </div>
                ))}
              </div>
            )
        )}
      </PanneauDossier>

      {relier && (
        <RelierSupport equipementCode={code} support={relier.support} libelle={relier.libelle} lignes={rep.lignes}
          onFerme={() => setRelier(null)} onFait={() => { void recharger(); }} />
      )}
      {ouverte && (
        <FenetreMaintenance e={ouverte} suivante={suivanteDe(ouverte)}
          onClose={() => { setOuverteCle(null); poserCibleDansAdresse({}); }}
          equipId={e.documentId} peutModifier={peutDeclarer} onModifie={() => void recharger()} onFiche={null} />
      )}
      {saisir !== null && (
        <SaisirMaintenance equipementFixe={code} typeInitial={saisir || undefined}
          onFerme={() => setSaisir(null)} onFait={() => { void recharger(); }} />
      )}
      {form && reg && (
        <FormulaireLigne reg={reg} initial={form.ligne ? brouillonDe(form.ligne) : { ...LIGNE_VIDE, equipementCode: code }}
          editId={form.ligne?.documentId ?? null} equipementFixe={code}
          onFerme={() => setForm(null)} onFait={() => { void recharger(); }} />
      )}
    </div>
  );
}

